import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import conversationService from '../services/conversationService';
import api from '../services/api';

/**
 * Custom hook to manage task menu state and actions
 */
const useTaskMenu = ({ projectId, project, showNotification }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTile, setSelectedTile] = useState(null);

  const handleMenuOpen = useCallback((event, tile) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedTile(tile);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedTile(null);
  }, []);

  const handleTileClick = useCallback((tile) => {
    try {
      const url = `/projects/${projectId}/conversation?task=${tile.id}`;
      window.open(url, '_blank');
    } catch (error) {
      showNotification({
        message: `Error navigating to ${tile.title}: ${error.message}`,
        type: 'error'
      });
    }
  }, [projectId, showNotification]);

  /**
   * Shared helper: find conversation + resolve version info for a task tile.
   * Returns { conversationId, versionNumber, title } or throws.
   */
  const resolveExportContext = useCallback(async (tile) => {
    const taskId = tile?.id || tile?._id || tile?.taskId || tile?.task_id;
    if (!taskId) throw new Error('Task ID not found');

    const conversations = await conversationService.getConversations(projectId);
    const conv = conversations.find(c => c.task_id === taskId || c.taskId === taskId);
    if (!conv) throw new Error('No content available for this task');

    const conversationId = conv._id || conv.id;
    const title = tile?.title || conv.title || 'Document';

    // Resolve active artifact version number
    let versionNumber = null;
    try {
      const resp = await api.get(`/conversations/${conversationId}/artifact/versions`, {
        params: { limit: 1 }
      });
      versionNumber = resp.data?.active_version_number || null;
    } catch {
      // No version info — export latest
    }

    return { conversationId, versionNumber, title };
  }, [projectId]);

  /**
   * Backend export helper — calls unified export API and triggers download.
   */
  const exportViaBackend = useCallback(async (format, conversationId, title, versionNumber) => {
    const response = await api.post(
      `/export/${format}/conversations/${conversationId}`,
      {
        title,
        include_toc: true,
        include_cover: true,
        include_diagrams: true,
        version_number: versionNumber || null,
        metadata: {
          subtitle: '',
          version: versionNumber ? String(versionNumber) : undefined,
        }
      },
      { responseType: 'blob' }
    );

    // Extract filename from Content-Disposition header or build one
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
    const safeTitle = title.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_') || 'export';
    const ext = format === 'pdf' ? '.pdf' : '.docx';
    const filename = filenameMatch?.[1] || `${safeTitle}${ext}`;

    // Trigger browser download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }, []);

  // Handler for downloading PDF (via backend DOCX→PDF pipeline)
  const handleDownloadPDF = useCallback(async (tile) => {
    try {
      showNotification({ message: `Downloading ${tile?.title} as PDF...`, type: 'info' });

      const { conversationId, versionNumber, title } = await resolveExportContext(tile);
      await exportViaBackend('pdf', conversationId, title, versionNumber);

      showNotification({ message: `${tile?.title} PDF downloaded successfully`, type: 'success' });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showNotification({ message: error.message || 'Failed to download PDF', type: 'error' });
    }
  }, [resolveExportContext, exportViaBackend, showNotification]);

  // Handler for downloading DOCX (via backend Ailyce-styled generator)
  const handleDownloadDOCX = useCallback(async (tile) => {
    try {
      showNotification({ message: `Downloading ${tile?.title} as DOCX...`, type: 'info' });

      const { conversationId, versionNumber, title } = await resolveExportContext(tile);
      await exportViaBackend('docx', conversationId, title, versionNumber);

      showNotification({ message: `${tile?.title} DOCX downloaded successfully`, type: 'success' });
    } catch (error) {
      console.error('Error downloading DOCX:', error);
      showNotification({ message: error.message || 'Failed to download DOCX', type: 'error' });
    }
  }, [resolveExportContext, exportViaBackend, showNotification]);

  const handleEdit = useCallback((tile) => {
    try {
      if (tile) {
        if (tile.id === 'chat-prd') {
          navigate(`/projects/${projectId}/conversation`);
        } else {
          navigate(
            `/projects/${projectId}/prd-workflow`,
            {
              state: {
                activeStep: tile.step || 0,
                preserveTasks: true,
                currentTasks: project?.tasks || []
              }
            }
          );
        }
      }
    } catch (error) {
      showNotification({
        message: `Error navigating to ${tile?.title}: ${error.message}`,
        type: 'error'
      });
    }
  }, [projectId, navigate, showNotification, project]);

  return {
    anchorEl,
    selectedTile,
    handleMenuOpen,
    handleMenuClose,
    handleTileClick,
    handleDownloadPDF,
    handleDownloadDOCX,
    handleEdit
  };
};

export default useTaskMenu;
